import React, { useState } from "react";
import PropTypes from "prop-types";
import iconEdit from "images/icons/edit.svg";
import iconConfirm from "images/icons/confirm.svg";
import iconCancel from "images/icons/cancel.svg";
import useSWR, { mutate } from "swr";
import useFetchApi from "hooks/useFetchApi";
import { useAuth } from "providers/AuthProvider";
import Loader from "components/Loader";
import { formatAmount } from "functions";

const ENDPOINT = "bonus-territorial";

/**
 * Valeur d'un bonus, editable en place par les administrateurs uniquement.
 * Les autres roles gestionnaires la voient en lecture seule.
 */
function FieldBlockBonus({ name, value, editable, onError }) {
  const [fetchApi] = useFetchApi();
  const [localValue, setLocalValue] = useState(value);
  const [edit, setEdit] = useState(false);

  const onSubmit = async () => {
    document.activeElement?.blur();
    const parsed = Number(String(localValue).replace(",", "."));
    if (localValue === "" || isNaN(parsed)) {
      setLocalValue(value);
      setEdit(false);
      return;
    }

    try {
      onError(null);
      // Le single type Strapi attend le corps encapsule dans `data`.
      await fetchApi(ENDPOINT, {
        method: "PUT",
        body: { data: { [name]: parsed } },
      });
      mutate(ENDPOINT);
    } catch ({ message }) {
      setLocalValue(value);
      onError(message ?? "La modification n'a pas pu être enregistrée.");
    } finally {
      setEdit(false);
    }
  };

  return (
    <div className="flex justify-between items-center">
      {!edit && <>{formatAmount(value)}/MWhc</>}
      {edit && (
        <>
          <input
            type="text"
            value={localValue}
            className='text-main-color w-[50px] p-1 border-1 border-2 border-sky-600 rounded outline-none"'
            onChange={(e) =>
              setLocalValue(e.target.value.replace(/[^0-9.,]/g, ""))
            }
          />
          €/MWhc
        </>
      )}

      {editable && !edit && (
        <div className="flex space-x-1">
          {/* On repart de la valeur courante : elle a pu changer depuis le
              dernier rendu (revalidation SWR, edition par un autre admin). */}
          <button
            onClick={() => {
              setLocalValue(value);
              setEdit(true);
            }}
          >
            <img src={iconEdit} alt="modifier" />
          </button>
        </div>
      )}

      {editable && edit && (
        <div className="flex space-x-1">
          <button onClick={onSubmit}>
            <img src={iconConfirm} alt="confirmer" />
          </button>
          <button
            onClick={() => {
              setEdit(false);
              setLocalValue(value);
            }}
          >
            <img src={iconCancel} alt="annuler" />
          </button>
        </div>
      )}
    </div>
  );
}
FieldBlockBonus.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  editable: PropTypes.bool.isRequired,
  onError: PropTypes.func.isRequired,
};

function Row({ statut, name, value, editable, onError }) {
  return (
    <div className="flex border-stone-400 border-0.5 rounded-lg px-4 py-5 text-xs text-light-color mt-2 items-center">
      <div className="w-[200px] pl-2 text-black">{statut}</div>
      <div className="w-[200px] pl-2">
        <FieldBlockBonus
          name={name}
          value={value}
          editable={editable}
          onError={onError}
        />
      </div>
    </div>
  );
}
Row.propTypes = {
  statut: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  editable: PropTypes.bool.isRequired,
  onError: PropTypes.func.isRequired,
};

export default function BonusTerritorial() {
  const [fetchApi] = useFetchApi();
  const { isAdmin } = useAuth();
  const [saveError, setSaveError] = useState(null);
  const { data, error, isLoading } = useSWR(ENDPOINT, fetchApi);

  if (isLoading || error) {
    return <Loader isLoading={isLoading} error={error} />;
  }

  // Strapi renvoie le single type sous `data.attributes`, et `pg` remonte les
  // colonnes `decimal` sous forme de chaine : on normalise en nombre.
  const bonus = data?.data?.attributes ?? {};
  const valeur = (champ) => Number(bonus[champ] ?? 0);

  return (
    <div className="w-[400px] mt-6">
      <div className="flex text-xs text-[#9E9E9E] px-3">
        <div className="w-[200px] pl-2">Bonus</div>
        <div className="w-[200px] pl-2">Valeur</div>
      </div>

      <Row
        statut="Bonus classique"
        name="bonus_classique"
        value={valeur("bonus_classique")}
        editable={isAdmin}
        onError={setSaveError}
      />
      <Row
        statut="Bonus précaire"
        name="bonus_precaire"
        value={valeur("bonus_precaire")}
        editable={isAdmin}
        onError={setSaveError}
      />

      {saveError && (
        <p className="mt-3 px-3 text-xs text-red-500">{saveError}</p>
      )}
    </div>
  );
}
